import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import jwt_decode from 'jwt-decode';

/**
 * Enable sending/receiving cookies.
 * Store user info in sessionStorage (tab-specific).
 * Set flag in localStorage so other tabs know user is authenticated.
 */
export const loginUser = createAsyncThunk(
  'users/login',
  async ({ email, password, rememberMe }, thunkAPI) => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_PORTAL_API + '/api/v1/auth',
        {
          email,
          password,
          rememberMe,
        },
        {
          withCredentials: true
        }
      );
      let data = await response.data;

      if (response.status === 200) {
        try {
          sessionStorage.setItem('user', JSON.stringify(data.user));
          sessionStorage.setItem('token', data.accessToken);
          sessionStorage.setItem('rememberMe', data.rememberMe);
          localStorage.setItem('isAuthenticated', 'true');
        } catch (e) {
          console.log(e);
        }
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

/**
 * Microsoft SSO Login.
 * Enable sending/receiving cookies.
 * Store user info in sessionStorage (tab-specific).
 * Set flag in localStorage so other tabs know user is authenticated.
 */
export const microsoftSSOLogin = createAsyncThunk(
  'users/ssoLogin',
  async ({ idToken, rememberMe }, thunkAPI) => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_PORTAL_API + '/api/v1/auth/sso-login',
        {
          idToken,
          rememberMe,
        },
        {
          withCredentials: true
        }
      );
      let data = await response.data;

      if (response.status === 200) {
        try {
          sessionStorage.setItem('user', JSON.stringify(data.user));
          sessionStorage.setItem('token', data.accessToken);
          sessionStorage.setItem('rememberMe', data.rememberMe);
          localStorage.setItem('isAuthenticated', 'true');
        } catch (e) {
          console.log(e);
        }
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response?.data || { message: 'SSO login failed' });
    }
  }
);

// Redux slice for auth
export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: {
      name: null,
      family_name: null,
      organization: null,
      company: null,
    },
    isFetching: false,
    isSuccess: false,
    isError: false,
    errorMessage: null,
  },
  reducers: {
    clearState: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
    },
  },
  extraReducers: {
    // Email/Password login
    [loginUser.fulfilled]: (state, { payload }) => {
      state.user = {
        name: payload.user.name,
        family_name: payload.user.family_name,
        organization: payload.user.organization,
        company: payload.user.company,
      };
      state.isFetching = false;
      state.isSuccess = true;
    },
    [loginUser.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload.message;
    },
    [loginUser.pending]: (state) => {
      state.isFetching = true;
    },

    // Microsoft SSO login
    [microsoftSSOLogin.fulfilled]: (state, { payload }) => {
      state.user = {
        name: payload.user.name,
        family_name: payload.user.family_name,
        organization: payload.user.organization,
        company: payload.user.company,
      };
      state.isFetching = false;
      state.isSuccess = true;
    },
    [microsoftSSOLogin.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload?.message || 'SSO login failed';
    },
    [microsoftSSOLogin.pending]: (state) => {
      state.isFetching = true;
    },
  },
});

export const { clearState } = authSlice.actions;

export const authSelector = (state) => state.auth;
